import { useIframePoolStore } from "@/components/Canvas/IframePool/iframePoolStore";
import {
  createInstantUpdateWithShell,
  detectHtmlHasShell,
  getContentsWithoutShell,
} from "@/components/Canvas/IframePool/utils";
import { updateCodebaseId } from "@/components/Canvas/Storyboard/StaticIframe/utils/utils";
import { getCodebaseIdFromNode } from "@/components/Canvas/utils/utils";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { v4 as uuidv4 } from "uuid";
import {
  DUMMY_CODEBASE_ID,
  EMPTY_TREE_CODEBASE_ID,
  SKIP_ROOT_CODEBASE_ID,
} from "./constants";
import { decompressNavTree } from "./navTreeCompression";
import { safeParseDocumentFromString } from "./safeHtmlParser/safeHtmlParser";
import { TempoElement } from "./tempoElement";
import { NavTreeNode, Storyboard } from "./types";

export const DEFAULT_INSTANT_UPDATE_HTML =
  "<div className='w-full h-full bg-white'/>";

export interface InstantUpdateData {
  html?: string;
  navTree?: NavTreeNode;
  compressedNavTreeNodes?: string[];
}

/**
 * Given, html and an optional nav tree (could be compressed),
 * returns:
 *
 * - The html with the correct shell for instant updates
 * - The nav tree (parsed from the html if not provided)
 * - Whether the default instant update html was used (true if html was empty or not provided)
 */
export const processInstantUpdateData = ({
  storyboardId,
  canvasId,
  projectSubfolderPath,
  instantUpdateData,
  wrapInShell,
}: {
  storyboardId: string;
  canvasId: string;
  projectSubfolderPath?: string;
  instantUpdateData?: InstantUpdateData;
  wrapInShell?: boolean;
}): {
  html?: string;
  navTree?: NavTreeNode | undefined;
  fileContents?: string;
  error?: string | undefined;
} => {
  // Do the instant update
  let htmlForInstantUpdate = instantUpdateData?.html;

  // If no html and no compressed nav tree, nothing to process
  if (
    !htmlForInstantUpdate &&
    !instantUpdateData?.compressedNavTreeNodes?.length
  ) {
    return {};
  }

  // Process HTML if provided
  if (htmlForInstantUpdate) {
    // Check if the html already has the shell
    const hasShell = detectHtmlHasShell(htmlForInstantUpdate);
    if (hasShell) {
      // Strip the shell so that we use the shell from this canvas
      htmlForInstantUpdate = getContentsWithoutShell(htmlForInstantUpdate);
    }

    if (wrapInShell) {
      const htmlShell = useIframePoolStore.getState().latestHtmlShell;
      if (htmlShell) {
        htmlForInstantUpdate = createInstantUpdateWithShell(
          htmlShell,
          htmlForInstantUpdate,
        );
      }
    }
  }

  let navTree: NavTreeNode | undefined = instantUpdateData?.navTree;
  if (
    !navTree &&
    !instantUpdateData?.compressedNavTreeNodes?.length &&
    htmlForInstantUpdate
  ) {
    // Parse nav tree from HTML when no nav tree or compressed nodes provided
    const expectedFilename = useCodebaseStore
      .getState()
      .getExpectedFilenameForStoryboard(storyboardId);
    const { newNavTree, newHtml } = getNavTreeFromHtml(
      htmlForInstantUpdate,
      storyboardId,
      expectedFilename,
      canvasId,
      projectSubfolderPath || null,
      true,
    );

    if (newNavTree) {
      navTree = newNavTree;
    }
    if (newHtml) {
      htmlForInstantUpdate = newHtml;
    }
  } else if (instantUpdateData?.compressedNavTreeNodes?.length) {
    const navTreeNodesToUse =
      instantUpdateData.compressedNavTreeNodes?.map((compressedNavTree) =>
        decompressNavTree(compressedNavTree),
      ) || [];

    navTree = navTreeNodesToUse[0] || undefined;
    if (navTreeNodesToUse?.length > 1) {
      // TODO: NEED TO DO BEFORE MERGING BUGBOT
      return {
        error: "Not yet supported, please fix!",
      };
    }
  }

  // Go through the nav tree and set new dummy codebase ids so that when the
  // prev id to new id map is applied the original codebase ids are not replaced
  // in the original location
  if (navTree) {
    updateAllStoryboardIdsInNavTree(navTree, storyboardId);
  }

  return {
    html: htmlForInstantUpdate,
    navTree,
  };
};

/**
 * Note: this works only if the html is created from plain react, e.g.:
 *
 * <div>
 *  <div>A</div>
 *  <div>B</div>
 * </div>
 *
 * NOT:
 *
 * ["A", "B"].map(item => <div>{item}</div>)
 *
 * Because the codebase IDs generated will not be correct. However, after a hot reload it'll be correct.
 *
 * This may also modify the html (e.g. inject codebase ids)
 */
export const getNavTreeFromHtml = (
  html: string,
  storyboardId: string,
  fullFilePath: string | null,
  canvasId: string,
  projectSubfolderPath: string | null,
  skipRootNodes?: boolean,
): {
  newNavTree: NavTreeNode | null;
  newHtml: string;
} => {
  try {
    const dummyDocument = safeParseDocumentFromString(html, "text/html");

    const { treeElementLookup } = useCodebaseStore.getState();

    const uniquePathsSkipped: string[] = [];
    let startNode: HTMLElement | null =
      dummyDocument.getElementsByTagName("body")[0];
    while (skipRootNodes && startNode) {
      if (
        startNode.tagName.toLowerCase() === "body" ||
        startNode.id === "root" ||
        startNode.id === "__next"
      ) {
        startNode = startNode.children[0] as HTMLElement;
        uniquePathsSkipped.push("0");
      } else {
        break;
      }
    }

    const navTreeResult = buildNavForNode({
      storyboardId: storyboardId,
      parent: undefined,
      node: startNode,
      uniquePathBase: "",
      uniquePathAddon: ["root", ...uniquePathsSkipped].join("-"),
      treeElements: treeElementLookup,
      domUniquePath: ["0", ...uniquePathsSkipped].join("-"),
    });

    const postProcess = (node: NavTreeNode | null, level: number) => {
      if (!node) {
        return;
      }

      // Remove the react fiber node after processing
      delete node["reactFiberNode"];

      node.level = level;
      node.children.forEach((child) => {
        postProcess(
          child,
          node.tempoElement.codebaseId === SKIP_ROOT_CODEBASE_ID
            ? level
            : level + 1,
        );
      });
    };

    postProcess(navTreeResult?.result || null, 0);

    return {
      newNavTree: navTreeResult?.result || null,
      newHtml: dummyDocument.documentElement.outerHTML,
    };
  } catch (e) {
    console.error("Error getting nav tree from html", e);
    return {
      newNavTree: null,
      newHtml: html,
    };
  }
};

interface BuildNavForNodeNonBlockingParams {
  storyboardId: string;
  parent: NavTreeNode | undefined;
  node: HTMLElement;

  // This unique path includes things like virtual components, etc
  uniquePathBase: string;
  uniquePathAddon: string;

  treeElements: { [codebaseId: string]: any };

  // This unique path is the path to the DOM element
  domUniquePath: string;
}

const getAllUnknownClasses = (node: any): string[] => {
  if (!node?.classList) {
    return [];
  }

  return Array.from(node.classList);
};

/**
 * As of Aug 13, 2025 it matches the bahavior inside tempo-devtools, BUT
 * it is also set up to eventually copy the behavior of completely so that
 * the tempo-devtools just sends info about the html and fiber tree, and the DOM
 * tree is constructed in the outer shell with that.
 *
 * That way, things like treeElements, etc will not need to be inside the iframes.
 */
const buildNavForNode = ({
  storyboardId,
  parent,
  node,
  uniquePathBase,
  uniquePathAddon,
  treeElements,

  domUniquePath,
}: BuildNavForNodeNonBlockingParams): {
  result: NavTreeNode | null;
} | null => {
  if (!node) {
    return null;
  }

  if (["noscript", "script", "style"].includes(node?.tagName?.toLowerCase())) {
    return null;
  }

  const foundId = getCodebaseIdFromNode(node);

  // Only call the id generator if it's not the body or root element to match what is expected
  // to show up in a new file
  let fallbackId = "";
  if (
    node?.tagName?.toLowerCase() !== "body" &&
    node?.id !== "root" &&
    node?.id !== "__next"
  ) {
    fallbackId = uuidv4(); // Generate a new ID
  }

  // May 15, 2023 -> found bug where a random iframe was being added with the hot reloaded code
  // I think this is related to this bug:
  // https://github.com/facebook/create-react-app/issues/11880
  if (node?.tagName?.toLowerCase() === "iframe") {
    if (!foundId) {
      node.remove();
      return null;
    }
  }

  // This node corresponds to the DOM element, not any components, unless we are collapsing into the component
  const navTreeNode: NavTreeNode = {
    parent,
    children: [],
    classList: getAllUnknownClasses(node),
    name: "",
    tempoElement: TempoElement.empty(),
  };

  parent?.children?.push(navTreeNode);
  navTreeNode.name = node.tagName;
  navTreeNode.elementTagName = node.tagName;

  // No components with static html
  navTreeNode.isComponent = false;

  const uniquePathForNode = `${uniquePathBase}${uniquePathAddon}`;

  const codebaseId = foundId || fallbackId;

  if (!foundId && fallbackId) {
    // Add the fallback as a data attribute on the node
    node.setAttribute("data-tempoelementid", fallbackId);
  }

  navTreeNode.tempoElement = new TempoElement(
    codebaseId,
    storyboardId,
    uniquePathForNode,
  );

  // TODO: how to get props? Maybe just get the fiber tree from the iframe..
  // navTreeNode.props = extractPropsFromReactFiberNode(reactFiberNode);

  // These are any children that are not other jsx elements
  const literalChildren: {
    index: number;
    value: any;
  }[] = [];

  Array.from(node.children).forEach((child, index) => {
    if (child.nodeType !== Node.TEXT_NODE) {
      return;
    }

    literalChildren.push({
      index,
      value: child.textContent,
    });
  });
  navTreeNode.literalChildren = literalChildren;
  navTreeNode.domUniquePath = domUniquePath;

  // Only parse children for non-svg elements
  if (node.children && node.tagName !== "svg") {
    const childrenWithoutScripts = Array.from(node.children).filter(
      (child: any) => {
        return child.tagName && child.tagName?.toLowerCase() !== "script";
      },
    );

    let index = 0;
    for (const child of childrenWithoutScripts) {
      buildNavForNode({
        storyboardId,
        parent: navTreeNode,
        node: child as HTMLElement,
        uniquePathBase: uniquePathForNode,
        uniquePathAddon: `-${index}`,
        treeElements,
        domUniquePath: `${domUniquePath}-${index}`,
      });
      index++;
    }
  }

  return {
    result: navTreeNode,
  };
};

export const updateAllStoryboardIdsInNavTree = (
  treeNode: NavTreeNode,
  storyboardId: string,
  transformCodebaseIdUsingThisId?: string,
) => {
  if (!treeNode) {
    return;
  }

  if (treeNode.tempoElement) {
    let newCodebaseId = treeNode.tempoElement.codebaseId;
    if (transformCodebaseIdUsingThisId) {
      newCodebaseId = updateCodebaseId(
        treeNode.tempoElement.codebaseId,
        transformCodebaseIdUsingThisId,
      );
    }
    treeNode.tempoElement = new TempoElement(
      newCodebaseId,
      storyboardId,
      treeNode.tempoElement.uniquePath,
    );
  }

  treeNode.children.forEach((child) => {
    updateAllStoryboardIdsInNavTree(
      child,
      storyboardId,
      transformCodebaseIdUsingThisId,
    );
  });
};

const KNOWN_COMPONENTS_TO_IGNORE = new Set(
  [
    "Symbol(react.strict_mode)",
    "Symbol(react.fragment)",
    "Symbol(react.provider)",
    "Symbol(react.context)",
    "Symbol(react.forward_ref)",
    "Symbol(react.memo)",
    "Symbol(react.lazy)",
    "Symbol(react.suspense)",
    "Symbol(react.error_boundary)",
    "BrowserRouter",
    "Routes",
  ].map((name) => name.toLowerCase()),
);

const KNOWN_NODE_NAMES_TO_IGNORE = new Set(
  ["body", "html", "head"].map((name) => name.toLowerCase()),
);

/**
 * Given a nav tree, filters out nodes that we don't want. Main logic is:
 * - Do not include nodes that are not directly in a non-application storyboard
 * - Do not include nodes without a codebase id
 */
export const filterOutNotNeededNodes = (
  finishedNavTree: NavTreeNode,
  storyboard: Storyboard,
): NavTreeNode => {
  const treeElements = useCodebaseStore.getState().treeElementLookup;

  let treeToReturn: NavTreeNode = finishedNavTree;

  /**
   * Returns whether the given tree element is in the top-level file of the storyboard
   * Note - for saved components the top-level file is the saved component file
   */
  const isElementDirectlyInStoryboard = (node: NavTreeNode) => {
    const filename = treeElements[node.tempoElement.codebaseId]?.filename;

    // Filter anything that is not in the storyboard itself or story source file
    return (
      Boolean(filename?.includes("tempobook/storyboards")) ||
      Boolean(
        filename &&
          (filename === storyboard?.fullFilePath ||
            filename?.replace(/\.(?:tsx?|jsx?)$/, "") ===
              storyboard?.storySourceComponentFilename?.replace(
                /\.(?:tsx?|jsx?)$/,
                "",
              )),
      )
    );
  };

  const isInViteMainFile = (node: NavTreeNode) => {
    const filename = treeElements[node.tempoElement.codebaseId]?.filename;
    // This can happen when the codebase id is outdated (e.g. the react fiber tree did not update even though the html did)
    // and so we cannot find the tree element that corresponds to this codebase id
    if (!filename) {
      throw new Error(
        `Tree element out of date for codebase id: ${node.tempoElement.codebaseId}`,
      );
    }
    const segments = filename?.split(".") || [];
    const pathBeforeExt = segments?.slice(0, -1)?.join(".");
    return pathBeforeExt?.endsWith("/main");
  };

  const processNode = (
    node: NavTreeNode,
    elementInStoryboardFound: boolean,
  ) => {
    // Process the children first
    for (let i = node.children.length - 1; i >= 0; i--) {
      processNode(
        node.children[i],
        elementInStoryboardFound || isElementDirectlyInStoryboard(node),
      );
    }

    // Product decision: Filter out nodes that don't exist in storyboard file for the corresponding component URL
    //
    // Historical context:
    // Dec 14 - a bug was found where in cases that components were dynamically loaded (e.g. in Next JS _app.tsx), when you click
    // on the top level component it would point to this location in the codebase:
    //
    // function MyApp({ Component, pageProps: { session, ...pageProps } }: AppProps) {
    //   return (
    //     <SessionProvider session={session}>
    //       <Component {...pageProps} />
    //       <Analytics />
    //     </SessionProvider>
    //   );
    // }
    //
    // This was especially an issue for component storyboards. Thus the decision was made to hide any top-level components or divs
    // that are not in the storyboard file
    const inComponentStoryboardAndSkip =
      storyboard?.type !== "APPLICATION" &&
      !elementInStoryboardFound &&
      !isElementDirectlyInStoryboard(node);

    // Does not map to somewhere in the codebase
    const invalidNode =
      !node.tempoElement.codebaseId?.startsWith("tempo-") || !node.name;

    // Known component or node to ignore
    const knownNodeToIgnore =
      (node.isComponent &&
        KNOWN_COMPONENTS_TO_IGNORE.has(node.name?.toLowerCase())) ||
      (!node.isComponent &&
        KNOWN_NODE_NAMES_TO_IGNORE.has(node.name?.toLowerCase()));

    // If this node doesn't have a codebaseId, move its children to its parent
    if (
      invalidNode ||
      knownNodeToIgnore ||
      isInViteMainFile(node) ||
      inComponentStoryboardAndSkip
    ) {
      if (node.parent) {
        // Move the children in the spot where the node was
        const childrenToMove = node.children;
        const indexOfNodeInParent = node.parent.children?.indexOf(node);

        node.parent.children.splice(indexOfNodeInParent, 1, ...childrenToMove);

        // Change the parent of all the children to the new parent
        childrenToMove.forEach((child) => {
          child.parent = node.parent;
        });

        // Special case - if the parent is a component we treat this like a forward ref and collapse the dom unique path in
        if (
          node.parent.tempoElement.codebaseId &&
          node.parent.isComponent &&
          !node.parent.domUniquePath
        ) {
          node.parent.domUniquePath = node.domUniquePath;
        }
      } else if (node.children.length === 1) {
        // This is the top-level node, move it down
        treeToReturn = node.children[0];

        treeToReturn.parent = undefined;
      } else if (node.children.length === 0) {
        // 0 children, no nav tree to return
        treeToReturn = {
          children: [],
          tempoElement: new TempoElement(
            EMPTY_TREE_CODEBASE_ID,
            storyboard?.id,
            "1",
          ),
          name: "",
        };
      } else if (!node.tempoElement.codebaseId) {
        // Make sure to set a dummy codebase id so it doesn't crash the app
        node.tempoElement = new TempoElement(
          DUMMY_CODEBASE_ID,
          node.tempoElement.storyboardId,
          node.tempoElement.uniquePath,
        );
      }
    }
  };

  processNode(
    finishedNavTree,
    false /* initialize as false, need to find the storyboard element first */,
  );

  const postProcess = (node: NavTreeNode, level: number) => {
    node.level = level;
    node.children.forEach((child) => {
      postProcess(
        child,
        node.tempoElement.codebaseId === SKIP_ROOT_CODEBASE_ID
          ? level
          : level + 1,
      );
    });
  };

  postProcess(treeToReturn, 0);
  return treeToReturn;
};
